/**
 * Хук для работы с n8n RAG Chat API
 */

import { useCallback, useEffect, useRef } from 'react';
import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { useUIStore } from '@/store/uiStore';
import { useToastStore } from '@/store/toastStore';
import { N8nApiService, HistoryMessage } from '@/services/api/n8n.api';
import { Message, MessageFile } from '@/types/message.types';
import { generateUUID } from '@/utils/uuid';
import { getStorageItem } from '@/utils/storage';
import { STORAGE_KEYS } from '@/constants/defaults';
import { analytics } from '@/utils/analytics';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Результат хука useN8nChat
 */
export interface UseN8nChatReturn {
  /** Массив сообщений */
  messages: Message[];
  
  /** Идет ли обработка */
  isTyping: boolean;
  
  /** Отправить сообщение (с опциональным массивом файлов и отдельным текстом для отображения) */
  sendMessage: (text: string, files?: File[] | File, displayText?: string) => Promise<void>;
  
  /** Остановить генерацию (не поддерживается в n8n) */
  stopGeneration: () => Promise<void>;
  
  /** Начать новую беседу */
  startNewConversation: () => void;
  
  /** Загружается ли файл */
  isUploading: boolean;
}

/**
 * Хук для управления чатом через n8n
 * 
 * @description Использует n8n webhook API вместо Dify.
 * Поддерживает текстовые сообщения и загрузку изображений.
 */
export function useN8nChat(): UseN8nChatReturn {
  // Stores
  const {
    messages,
    isTyping,
    userId,
    setConversationId,
    getConversationId,
    addMessage,
    updateMessage,
    setIsTyping,
    startNewConversation: startNew,
  } = useChatStore();

  const { config, language } = useConfigStore();
  const { setUploadProgress, uploadProgress } = useUIStore();
  const { showError } = useToastStore();

  // Refs
  const apiRef = useRef<N8nApiService | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Инициализация API Service
  // Если baseURL пустой — используем относительный путь (для Vite proxy)
  useEffect(() => {
    const baseURL = config.baseURL || window.location.origin;
    const chatEndpoint = config.chatEndpoint || `${baseURL}/webhook/chat`;
    
    apiRef.current = new N8nApiService({
      baseURL: baseURL,
      chatEndpoint: chatEndpoint,
      timeout: 90000,
      debug: config.debug,
    });
    
    chatLogger.log('[useN8nChat] API initialized:', { baseURL, chatEndpoint });
  }, [config.baseURL, config.chatEndpoint, config.debug]);

  /**
   * Отправка сообщения
   * Поддерживает как один файл (обратная совместимость), так и массив файлов
   */
  const sendMessage = useCallback(
    async (text: string, filesOrFile?: File[] | File, displayText?: string) => {
      if (!apiRef.current) {
        const apiErrors: Record<string, string> = {
          ru: 'API не настроен. Проверьте конфигурацию.',
          en: 'API not configured. Check your settings.',
          de: 'API nicht konfiguriert. Überprüfen Sie die Einstellungen.',
        };
        showError(apiErrors[language] || apiErrors.en);
        return;
      }

      const files: File[] = filesOrFile
        ? Array.isArray(filesOrFile)
          ? filesOrFile
          : [filesOrFile]
        : [];

      if (!text.trim() && files.length === 0) return;

      // Тестовый режим: [review "CODE"] — сразу показать кнопки без ИИ
      // Normalize smart/curly quotes before matching
      const normalizedText = text.trim().replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/g, '"');
      const reviewTestMatch = normalizedText.match(/^\[review\s+"([^"]+)"\]$/i);
      if (reviewTestMatch) {
        const kitCode = reviewTestMatch[1];
        addMessage({
          id: generateUUID(),
          content: text.trim(),
          sender: 'user',
          timestamp: Date.now(),
          status: 'sent',
        });
        addMessage({
          id: generateUUID(),
          content: `Отзывы по комплекту:\n[review "${kitCode}"]`,
          sender: 'assistant',
          timestamp: Date.now(),
        });
        return;
      }

      // Отменяем предыдущий незавершённый запрос
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        chatLogger.log('[useN8nChat] Sending:', { text, filesCount: files.length });

        const uploadedFiles: MessageFile[] = [];

        if (files.length > 0) {
          setUploadProgress(10);

          for (let i = 0; i < files.length; i++) {
            const file = files[i];

            chatLogger.log(`[useN8nChat] File ${i + 1}/${files.length}:`, file.name);

            const blobUrl = URL.createObjectURL(file);
            uploadedFiles.push({
              id: generateUUID(),
              type: 'image',
              url: blobUrl,
              name: file.name,
              size: file.size,
              mimeType: file.type,
              belongsTo: 'user',
            });
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

            setUploadProgress(Math.round(((i + 1) / files.length) * 80) + 10);
          }

          setUploadProgress(100);
          await new Promise((resolve) => setTimeout(resolve, 300));
          setUploadProgress(0);
        }

        const photoLabels: Record<string, (n: number) => string> = {
          ru: (n) => n === 1 ? '📷 Фото для анализа' : `📷 ${n} фото для анализа`,
          en: (n) => n === 1 ? '📷 Photo for analysis' : `📷 ${n} photos for analysis`,
          de: (n) => n === 1 ? '📷 Foto zur Analyse' : `📷 ${n} Fotos zur Analyse`,
        };
        const photosLabel = (photoLabels[language] || photoLabels.en)(files.length);
        const shownText = displayText || text.trim() || (files.length > 0 ? photosLabel : '');
        
        const userMessage: Message = {
          id: generateUUID(),
          content: shownText,
          sender: 'user',
          timestamp: Date.now(),
          status: 'sending',
          files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        };

        addMessage(userMessage);
        updateMessage(userMessage.id, { status: 'sent' });

        setIsTyping(true);

        let currentMessages = useChatStore.getState().messages;
        if (currentMessages.length <= 1) {
          const stored = getStorageItem<Message[]>(STORAGE_KEYS.MESSAGES_HISTORY, []);
          if (stored.length > currentMessages.length) {
            currentMessages = stored;
          }
        }
        const history: HistoryMessage[] = currentMessages.slice(-10).map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.content,
          timestamp: msg.timestamp,
        }));

        const currentConvId = getConversationId();
        const activeConversationId = currentConvId || generateUUID();
        if (!currentConvId) {
          chatLogger.warn('[useN8nChat] conversationId was null — generated new:', activeConversationId);
          setConversationId(activeConversationId);
        }

        const sendRequest = async (signal: AbortSignal) => {
          if (files.length > 0) {
            return apiRef.current!.sendMessageWithImage({
              message: text.trim() || config.photoAnalysisPrompt || 'Identify this car and check LARTE Design kit compatibility',
              imageFile: files[0],
              language,
              userId,
              conversationId: activeConversationId,
              history,
            }, signal);
          }
          return apiRef.current!.sendMessage({
            message: text.trim(),
            language,
            userId,
            conversationId: activeConversationId,
            history,
          }, signal);
        };

        let response;
        try {
          response = await sendRequest(controller.signal);
        } catch (firstError) {
          if (controller.signal.aborted) throw firstError;
          chatLogger.warn('[useN8nChat] First attempt failed, retrying in 2s…', firstError);
          await new Promise(r => setTimeout(r, 2000));
          if (controller.signal.aborted) throw firstError;
          response = await sendRequest(controller.signal);
        }

        if (controller.signal.aborted) return;

        setIsTyping(false);

        const n8nConversationId = response.conversationId || response.id;
        if (
          n8nConversationId
          && n8nConversationId !== 'null'
          && n8nConversationId !== 'undefined'
          && n8nConversationId !== activeConversationId
        ) {
          chatLogger.warn('[useN8nChat] n8n returned different conversationId:', n8nConversationId, '(was:', activeConversationId, ')');
          setConversationId(n8nConversationId);
        }

        chatLogger.log('[useN8nChat] Response:', response);

        let responseText = response.response || response.output || '';
        let showLeadForm = response.showLeadForm === true || response.lead === true;
        let leadFormReason = response.leadFormReason;

        // Parse SHOWLEADTRUE / SHOWLEADFALSE markers from text
        if (/SHOWLEADTRUE/i.test(responseText)) {
          showLeadForm = true;
          leadFormReason = leadFormReason || 'intent_detected';
          responseText = responseText.replace(/\s*SHOWLEADTRUE\s*/gi, '').trim();
        }
        responseText = responseText.replace(/\s*SHOWLEADFALSE\s*/gi, '').trim();

        const aiMessage: Message = {
          id: generateUUID(),
          content: responseText,
          sender: 'assistant',
          timestamp: Date.now(),
          metadata: {
            sourcesCount: response.sources_count,
            carInfo: response.carInfo,
            fallbackForm: showLeadForm,
            leadFormReason,
            buttons: response.buttons,
          },
        };

        analytics.aiResponseReceived(
          !!(response.buttons?.length),
          showLeadForm,
        );

        addMessage(aiMessage);
        config.onMessage?.(aiMessage);

      } catch (error) {
        if (controller.signal.aborted) return;

        const axiosErr = error as { response?: { status?: number; data?: unknown }; code?: string };
        chatLogger.error('[useN8nChat] Error:', {
          message: error instanceof Error ? error.message : String(error),
          code: axiosErr.code,
          httpStatus: axiosErr.response?.status,
          responseData: axiosErr.response?.data,
        });

        setIsTyping(false);
        setUploadProgress(0);
        
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorPrefixes: Record<string, string> = {
          ru: 'Не удалось получить ответ',
          en: 'Failed to get response',
          de: 'Antwort konnte nicht abgerufen werden',
        };
        showError(`${errorPrefixes[language] || errorPrefixes.en}: ${errorMessage}`);

        const fallback =
          (config.fallbackMessages && (config.fallbackMessages as any)[language]) ||
          config.fallbackMessages?.en ||
          'I cannot respond right now. Please leave your contact and we will get back to you.';

        const aiMessage: Message = {
          id: generateUUID(),
          content: fallback,
          sender: 'assistant',
          timestamp: Date.now(),
          metadata: { fallbackForm: true },
        };

        addMessage(aiMessage);
        config.onError?.(error instanceof Error ? error : new Error(errorMessage));
      }
    },
    [
      language,
      config.debug,
      config.onMessage,
      config.onError,
      config.fallbackMessages,
      addMessage,
      updateMessage,
      setIsTyping,
      setUploadProgress,
      showError,
    ]
  );

  const stopGeneration = useCallback(async () => {
    abortRef.current?.abort();
    setIsTyping(false);
  }, [setIsTyping]);

  const startNewConversation = useCallback(() => {
    abortRef.current?.abort();
    startNew();
  }, [startNew]);

  return {
    messages,
    isTyping,
    sendMessage,
    stopGeneration,
    startNewConversation,
    isUploading: uploadProgress > 0 && uploadProgress < 100,
  };
}

